import { useRouter } from 'vue-router';
import { useCheckoutStore } from '@/stores/checkout';
import { resolveOfferingCanvasCourseId } from '@/utils/ga4Items';

/**
 * Composable for handling common course actions.
 *
 * Encapsulates checkout navigation, course URL opening, and action routing
 * so that CourseCardCompact, EnrolledCertificateCard, and other consumers
 * don't duplicate this logic.
 *
 * @param {Object} options
 * @param {Function} [options.onReload] - Callback when a reload is needed
 * @param {Function} [options.onReloadDashboardOrPathway] - Callback for dashboard/pathway reload
 * @returns {{ navigateToCheckout: Function, openCourseUrl: Function, handleButtonAction: Function }}
 */
export function useCourseActions(options = {}) {
  const router = useRouter();
  const checkoutStore = useCheckoutStore();

  /**
   * Navigate to checkout page after populating checkout store.
   * Mirrors the deeplink flow used in Dashboard.vue and EnrolledCertificateCard.
   *
   * The router's `beforeEnter` guard on the `courseCheckout` route handles the
   * Stripe cart clear, so callers don't have to.
   *
   * @param {Object} course - Full course object from V2 API
   * @param {Object} offeringData - { courseOfferingId, courseStartDate } from dropdown selection
   * @param {string} [from='certificates'] - Source context for checkout navigation
   */
  const navigateToCheckout = (course, offeringData, from = 'certificates') => {
    const selectedOfferingId = String(offeringData.courseOfferingId || '');
    const selectedOffering = course.offerings?.find((o) => String(o.offeringId) === selectedOfferingId);
    const openEdxCourseId =
      selectedOffering?.openEdxCourseId || course.offering?.openEdxCourseId || course.lms?.openEdxCourseId || '';

    checkoutStore.setCheckoutData({
      title: course.courseName || '',
      coursePrefix: course.coursePrefix || '',
      coursePrefixDisplayLabel: course.coursePrefixDisplayLabel || course.coursePrefix || '',
      courseStartDate: offeringData.courseStartDate || '',
      courseEndDate: offeringData.courseEndDate || selectedOffering?.endDate || course.offering?.endDate || '',
      courseType: course.courseType || '',
      credits: course.credits || 0,
      courseOfferingId: selectedOfferingId,
      courseOfferingName: openEdxCourseId,
      sessionName: selectedOffering?.sessionName || '',
      enrollmentId: '0',
      enrollmentType: 'new',
      buttonLabel: 'Access Course',
      buttonAction: 'Access Course',
      creditEligibilityPrice: course.payment?.creditEligibilityPrice || 0,
      benefitsEligible: false,
      benefitType: '',
      canvasEnrollmentId: 0,
      canvasUserId: course.lms?.canvasUserId || null,
      canvasCourseId: resolveOfferingCanvasCourseId(selectedOffering || course.offering, course.lms),
      from,
      courseInfo: { _openEdxCourseId: openEdxCourseId }
    });

    router.push({ name: 'courseCheckout' });
  };

  /**
   * Open course catalog URL in a new tab.
   *
   * @param {Object} course - Course object with offering.eaCourseUrl
   */
  const openCourseUrl = (course) => {
    const url = course.offering?.eaCourseUrl;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  /**
   * Handle button-action events from compact cards.
   * Emits structured data that parents can act on.
   *
   * @param {Object} course - Course object
   * @returns {Object} Action payload with courseId, coursePrefix, action, enrollmentId, courseOfferingId
   */
  const buildActionPayload = (course) => {
    return {
      courseId: course.courseId,
      coursePrefix: course.coursePrefix,
      action: course.ui?.buttonAction,
      enrollmentId: course.enrollment?.enrollmentId,
      courseOfferingId: course.offering?.courseOfferingId
    };
  };

  return {
    navigateToCheckout,
    openCourseUrl,
    buildActionPayload
  };
}
